import shortID from 'shortid'
import { ChannelType } from '../../models/channelType'
import isInternetExplorer from '../../util/internetExplorerHelper'
import {
  CLEAR_TRANSACTION_DETAILS,
  CREDIT_TRANSACTION_SUCCESS,
  GET_CART_SUMMARY_FAILED,
  GET_CART_SUMMARY_SUCCESS,
  GET_TRANSACTION_DETAILS,
  NOTIFY_DANGER,
  NOTIFY_SUCCESS,
  RESET_TRANSACTIONS,
  SET_CURRENT_TRANSACTION_ID,
  SET_TERMINAL_TRANSACTION,
  TRANSACTION_DEBIT_LOADING,
  TRANSACTION_DETAILS_ACTION_ACTIVE,
  TRANSACTION_DETAILS_ACTION_FAILED,
  TRANSACTION_DETAILS_ACTION_SUCCESS,
  TRANSACTION_HAS_INVOICE_REF_ACTIVE,
  TRANSACTION_HAS_INVOICE_REF_CANCEL,
  TRANSACTION_HAS_INVOICE_REF_FAILED,
  TRANSACTION_HAS_INVOICE_REF_SUCCESS,
} from './action-types'
import { request } from './auth-actions'
import { genericError, silentError } from './action-utils'
import paymentEngineApi from '../../api/payment-engine-api'
import paymentEngineSearchApi from '../../api/payment-engine-search-api'
import representativeServiceApi from '../../api/representative-service-api'
import {
  addToCartRequest,
  annulTransactionRequest,
  creditTransactionRequest,
  debitTransactionRequest,
} from '../../api/request-builder/payment-engine-request-builder'
import { PaymentMethods } from '../../models/payment-methods'
import { notify } from './notification-actions'
import { getText } from '../../internal/TextLabels'
import TransactionServiceApi from '../../api/transaction-service-api'
import { getStoreId, getToken } from '../../util/helpers'
import {
  PaymentMethodDescriptions,
  PaymentMethodFallbackIds,
} from '../../util/transactionConstants'
import { sendTransactionSearchEvent } from '../../util/ga-helpers'

export const debitTransactionSuccess = response => ({
  type: GET_TRANSACTION_DETAILS,
  response,
})

export const creditTransactionSuccess = response => ({
  type: GET_TRANSACTION_DETAILS,
  response,
})

export const creditTerminalTransactionSuccess = response => ({
  type: CREDIT_TRANSACTION_SUCCESS,
  response,
})

export const annulTransactionSuccess = response => ({
  type: GET_TRANSACTION_DETAILS,
  response,
})

export const getTransactionDetailsSuccess = response => ({
  type: GET_TRANSACTION_DETAILS,
  response,
})

export const clearTransactionDetailsSuccess = () => ({
  type: CLEAR_TRANSACTION_DETAILS,
})

export const addToCartTransactionSuccess = response => ({
  type: GET_TRANSACTION_DETAILS,
  response,
})

export const setCurrentTransactionIdSuccess = id => ({
  type: SET_CURRENT_TRANSACTION_ID,
  id,
})

export const setCurrentTransaction = transaction => ({
  type: SET_TERMINAL_TRANSACTION,
  transaction,
})

export const getCartSummarySuccess = response => ({
  type: GET_CART_SUMMARY_SUCCESS,
  response,
})

export const getCartSummaryFailed = () => ({
  type: GET_CART_SUMMARY_FAILED,
})

export const transactionActionLoading = () => ({
  type: TRANSACTION_DETAILS_ACTION_ACTIVE,
})
export const transactionActionFailed = () => ({
  type: TRANSACTION_DETAILS_ACTION_FAILED,
})
export const transactionActionSuccess = () => ({
  type: TRANSACTION_DETAILS_ACTION_SUCCESS,
})
export const transactionHasInvoiceRefActive = () => ({
  type: TRANSACTION_HAS_INVOICE_REF_ACTIVE,
})

export const transactionHasInvoiceRefSuccess = response => ({
  type: TRANSACTION_HAS_INVOICE_REF_SUCCESS,
  response,
})

export const transactionHasInvoiceRefFailed = () => ({
  type: TRANSACTION_HAS_INVOICE_REF_FAILED,
})

export const cancelOngoingAction = () => ({
  type: TRANSACTION_HAS_INVOICE_REF_CANCEL,
})

export const resetTransactions = () => ({ type: RESET_TRANSACTIONS })

export const debitTransactionLoading = status => ({
  type: TRANSACTION_DEBIT_LOADING,
  status,
})

const getCorrectDescription = paymentMethod => {
  return (
    PaymentMethodDescriptions[paymentMethod.id] || paymentMethod.description
  )
}

export const searchTransactions =
  (pageInfo, searchTerm, combinedFilters) => dispatch =>
    dispatch(
      request(paymentEngineSearchApi.searchTransactions, {
        pageInfo,
        searchTerm,
        filters: combinedFilters,
      }),
    )
      .then(data => {
        const updatedTransactions = data.results.map(transaction => {
          if (
            Object.values(PaymentMethodFallbackIds).includes(
              transaction.paymentMethod.id,
            )
          ) {
            return {
              ...transaction,
              paymentMethod: {
                ...transaction.paymentMethod,
                description: getCorrectDescription(transaction.paymentMethod),
              },
            }
          }

          return transaction
        })
        sendTransactionSearchEvent(updatedTransactions)

        return {
          ...data,
          results: updatedTransactions,
        }
      })
      .catch(error => {
        genericError(error)
        throw error
      })

export const debitTransactions =
  (transactions, hasInvoiceNumber) => (dispatch, getState) => {
    dispatch(debitTransactionLoading(true))
    const arrayOfPromises = []
    transactions.forEach(transaction => {
      const req = {
        channelType: ChannelType.MERCHANT_PORTAL,
        creator: getState().auth.username,
        invoiceData: {
          deliveryType: 'EMAIL',
          invoiceId: transaction.invoiceNumber,
        },
      }
      if (
        hasInvoiceNumber ||
        transaction.paymentMethod.type !== PaymentMethods.RESURS_INVOICE
      ) {
        delete req.invoiceData.invoiceId
      }
      const debitRequest = {
        paymentId: transaction.id,
        request: JSON.stringify(req),
      }
      arrayOfPromises.push(
        dispatch(request(paymentEngineApi.payment.debit, debitRequest))
          .then(response => {
            return {
              id: response.externalId,
            }
          })
          .catch(error => {
            return {
              error,
            }
          }),
      )
    })
    return Promise.all(arrayOfPromises)
      .catch(error => genericError(error))
      .then(response => {
        const invoiceNumbers = []

        response.forEach(res => {
          if (
            res.error &&
            res.error.data &&
            res.error.data.code === 'INVOICE_ID_ALREADY_EXISTS' &&
            res.error.data.parameters &&
            res.error.data.parameters.INVOICE_NUMBER
          ) {
            invoiceNumbers.push(res.error.data.parameters.INVOICE_NUMBER)
          }
        })

        if (invoiceNumbers.length) {
          notify(
            NOTIFY_DANGER,
            getText(
              'transactions.errors.multipleInvoiceIDsInUse',
              invoiceNumbers.join(', '),
            ),
          )
        }
        dispatch(debitTransactionLoading(false))
      })
  }

export const getTransactionDetails = id => dispatch => {
  return dispatch(request(paymentEngineApi.payment.get, id))
    .then(data => {
      dispatch(getTransactionDetailsSuccess(data))
    })
    .catch(error => {
      genericError(error)
      throw error
    })
}

export const clearTransactionDetails = () => dispatch => {
  return dispatch(clearTransactionDetailsSuccess())
}

export const setCurrentTransactionId = id => dispatch => {
  dispatch(setCurrentTransactionIdSuccess(id))
}

export const getCartSummary = id => dispatch => {
  return dispatch(request(paymentEngineApi.payment.getCartSummary, id))
    .then(data => {
      dispatch(getCartSummarySuccess(data))
    })
    .catch(error => {
      genericError(error)
      dispatch(getCartSummaryFailed())
      throw error
    })
}

export const addToCart = (cartData, id) => (dispatch, getState) => {
  dispatch(transactionActionLoading())
  const addRequest = {
    paymentId: id,
    request: addToCartRequest(
      cartData,
      id,
      getState().transactions.currentTransactionDetails,
      getState().auth,
    ),
  }
  return dispatch(request(paymentEngineApi.payment.addToCart, addRequest))
    .then(response => {
      dispatch(addToCartTransactionSuccess(response))
      dispatch(getCartSummary(getState().transactions.currentTransactionId))
      dispatch(transactionActionSuccess())
      notify(NOTIFY_SUCCESS, getText('transactions.transaction.add.success'))
    })
    .catch(error => {
      dispatch(transactionActionFailed())
      silentError(error)
      notify(
        NOTIFY_DANGER,
        getText('transactions.transaction.add.errorFailedToAdd'),
      )

      throw error
    })
}

export const debitTransaction =
  (cartData, id, invoiceRef) => (dispatch, getState) => {
    dispatch(transactionActionLoading())
    const debitRequest = {
      paymentId: id,
      request: debitTransactionRequest(
        cartData,
        getState().transactions.currentTransactionDetails,
        getState().auth,
        invoiceRef,
      ),
    }
    return dispatch(request(paymentEngineApi.payment.debit, debitRequest))
      .then(response => {
        dispatch(debitTransactionSuccess(response))
        dispatch(getCartSummary(getState().transactions.currentTransactionId))
        dispatch(transactionActionSuccess())
      })
      .catch(error => {
        silentError(error)
        if (error.data && error.data.code) {
          notify(
            NOTIFY_DANGER,
            getText(`transactions.errors.${error.data.code}`),
          )
        } else {
          genericError()
        }

        dispatch(transactionActionFailed())
        throw error
      })
  }

export const getCreditTerminalTransaction =
  (accountNumber, transactionId) => dispatch => {
    return dispatch(
      request(
        TransactionServiceApi.getCreditTransactions(
          accountNumber,
          transactionId,
        ),
      ),
    )
      .then(data => {
        dispatch(getTransactionDetailsSuccess(data))
      })
      .catch(error => {
        genericError(error)
        throw error
      })
  }

export const creditTerminalTransaction =
  (amount, currentTransaction) => dispatch => {
    dispatch(transactionActionLoading())
    const creditRequest = {
      amountWithVat: amount,
      accountNumber: currentTransaction.accountNumber,
      externalReference: shortID.generate(),
      storeId: currentTransaction.storeId,
      transactionId: currentTransaction.transactionId,
      booked: currentTransaction.booked,
    }
    return dispatch(
      request(TransactionServiceApi.creditTransaction, creditRequest),
    )
      .then(() => {
        dispatch(creditTerminalTransactionSuccess(creditRequest))
        dispatch(
          getCreditTerminalTransaction(
            creditRequest.accountNumber,
            creditRequest.transactionId,
          ),
        )
        dispatch(transactionActionSuccess())
        notify(
          NOTIFY_SUCCESS,
          getText('transactions.transaction.creditTerminal.success'),
        )
      })
      .catch(error => {
        silentError(error)
        notify(
          NOTIFY_DANGER,
          getText('transactions.transaction.creditTerminal.failed'),
        )

        dispatch(transactionActionFailed())
        throw error
      })
  }

export const creditTransaction =
  (cartData, id, invoiceRef) => (dispatch, getState) => {
    dispatch(transactionActionLoading())

    const creditRequest = {
      paymentId: id,
      request: creditTransactionRequest(
        cartData,
        getState().transactions.currentTransactionDetails,
        getState().auth,
        invoiceRef,
      ),
    }
    return dispatch(request(paymentEngineApi.payment.credit, creditRequest))
      .then(response => {
        dispatch(creditTransactionSuccess(response))
        dispatch(getCartSummary(getState().transactions.currentTransactionId))
        dispatch(transactionActionSuccess())
      })
      .catch(error => {
        silentError(error)
        if (error.data && error.data.code) {
          notify(
            NOTIFY_DANGER,
            getText(`transactions.errors.${error.data.code}`),
          )
        } else {
          genericError()
        }
        dispatch(transactionActionFailed())
        throw error
      })
  }

export const annulTransaction = (cartData, id) => (dispatch, getState) => {
  dispatch(transactionActionLoading())

  const annulRequest = {
    paymentId: id,
    request: annulTransactionRequest(
      cartData,
      getState().transactions.currentTransactionDetails,
      getState().auth,
    ),
  }
  return dispatch(request(paymentEngineApi.payment.annul, annulRequest))
    .then(response => {
      dispatch(annulTransactionSuccess(response))
      dispatch(getCartSummary(getState().transactions.currentTransactionId))
      dispatch(transactionActionSuccess())
    })
    .catch(error => {
      silentError(error)
      notify(
        NOTIFY_DANGER,
        getText('transactions.errors.unableToAnnulTransaction'),
      )

      dispatch(transactionActionFailed())
      throw error
    })
}

export const hasInvoiceRef = () => (dispatch, getState) => {
  dispatch(transactionHasInvoiceRefActive())
  return dispatch(
    request(
      representativeServiceApi.getNextInvoiceNumber,
      getStoreId(getState()),
      getToken(getState()),
    ),
  )
    .then(response => {
      const required = !(response && response.result)
      dispatch(transactionHasInvoiceRefSuccess(required))
    })
    .catch(error => {
      if (error.status === 404) {
        const required = true
        dispatch(transactionHasInvoiceRefSuccess(required))
      } else {
        silentError(error)
        genericError()
        dispatch(transactionHasInvoiceRefFailed())
        throw error
      }
    })
}

export const fetchInvoicePdf = (pdfId, documentName) => dispatch =>
  dispatch(request(paymentEngineApi.getInvoicePdf(pdfId)))
    .then(b => {
      const blob = new Blob([b], { type: 'application/pdf' })
      if (isInternetExplorer()) {
        window.navigator.msSaveOrOpenBlob(blob, `${documentName}.pdf`)
        return null
      }
      return URL.createObjectURL(blob)
    })
    .catch(error => silentError(error))

export const resendInvoice =
  ({ paymentId, pdfId, email }) =>
  dispatch =>
    dispatch(request(paymentEngineApi.resendInvoice(paymentId, pdfId, email)))
      .then(() => {
        notify(
          NOTIFY_SUCCESS,
          getText('transactions.transaction.history.resendInvoiceSuccess'),
        )
      })
      .catch(error => silentError(error))
